/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { ApolloLink } from "@apollo/client/core";

const RATE_LIMIT_WARN_THRESHOLD = 0.75;
const RATE_LIMIT_ERROR_THRESHOLD = 0.9;
const COST_WARN_THRESHOLD = 0.75;
const COST_ERROR_THRESHOLD = 0.9;

const SESSION_STORAGE_KEY = "rate-limit-debug-queries";

const debuggedQueries = new Set<string>(loadDebuggedQueriesFromSession());

function loadDebuggedQueriesFromSession(): string[] {
    try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function persistDebuggedQueries() {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...debuggedQueries]));
    } catch {
        // sessionStorage unavailable (e.g. SSR) — ignore silently
    }
}

export function enableQueryCostDebug(...queryNames: string[]) {
    queryNames.forEach((name) => debuggedQueries.add(name));
    persistDebuggedQueries();
}

export function disableQueryCostDebug(...queryNames: string[]) {
    if (queryNames.length === 0) {
        debuggedQueries.clear();
    } else {
        queryNames.forEach((name) => debuggedQueries.delete(name));
    }
    persistDebuggedQueries();
}

export function getDebuggedQueries(): ReadonlySet<string> {
    return debuggedQueries;
}

interface RateLimitHeaders {
    rateLimit: number;
    rateLimitConsumed: number;
    rateLimitRemaining: number;
    rateLimitTimeUntilReset: number;
    costCurrentQuery: number;
    costLimitPerQuery: number;
}

// --- Query count tracking per rate-limit time slot ---

interface QuerySlotTracker {
    queryCount: number;
    totalCost: number;
    slotResetTime: number; // timestamp (ms) when the current slot expires
}

const slotTracker: QuerySlotTracker = { queryCount: 0, totalCost: 0, slotResetTime: 0 };

function updateSlotTracker(info: RateLimitHeaders) {
    const now = Date.now();
    const slotEnd = now + info.rateLimitTimeUntilReset;

    // If the reset time jumped forward significantly, a new slot started — reset counters
    if (now > slotTracker.slotResetTime) {
        slotTracker.queryCount = 0;
        slotTracker.totalCost = 0;
        slotTracker.slotResetTime = slotEnd;
    }

    slotTracker.queryCount++;
    slotTracker.totalCost += info.costCurrentQuery;
}

export function getSlotTracker(): Readonly<QuerySlotTracker> {
    return slotTracker;
}

function parseRateLimitHeaders(headers: Headers): RateLimitHeaders | null {
    const rateLimit = Number(headers.get("rate-limit"));
    if (!rateLimit) return null;

    return {
        rateLimit,
        rateLimitConsumed: Number(headers.get("rate-limit-consumed")) || 0,
        rateLimitRemaining: Number(headers.get("rate-limit-remaining")) || 0,
        rateLimitTimeUntilReset: Number(headers.get("rate-limit-time-until-reset")) || 0,
        costCurrentQuery: Number(headers.get("cost-current-query")) || 0,
        costLimitPerQuery: Number(headers.get("cost-limit-per-query")) || 0,
    };
}

/**
 * Logs the current rate limit status - therefore the limit across all queries, not just the current one.
 * "How much can I still query before hitting the rate limit?"
 *
 * @param operationName Query name
 * @param info
 */
function logRateLimitStatus(operationName: string, info: RateLimitHeaders) {
    const consumedRatio = info.rateLimitConsumed / info.rateLimit;
    const resetSeconds = (info.rateLimitTimeUntilReset / 1000).toFixed(1);

    if (consumedRatio >= RATE_LIMIT_ERROR_THRESHOLD) {
        console.debug(
            `[Rate Limit] CRITICAL: ${(consumedRatio * 100).toFixed(1)}% consumed` +
                ` (${info.rateLimitRemaining} remaining, resets in ${resetSeconds}s)` +
                ` — Query: ${operationName}`
        );
    } else if (consumedRatio >= RATE_LIMIT_WARN_THRESHOLD) {
        console.debug(
            `[Rate Limit] WARNING: ${(consumedRatio * 100).toFixed(1)}% consumed` +
                ` (${info.rateLimitRemaining} remaining, resets in ${resetSeconds}s)` +
                ` — Query: ${operationName}`
        );
    }
}

/**
 * Logs the current cost status of the query, based on the cost limit for that query.
 * "How expensive is this query?"
 *
 * @param operationName Query name
 * @param info
 */
function logQueryCostStatus(operationName: string, info: RateLimitHeaders) {
    if (info.costLimitPerQuery <= 0 || info.costCurrentQuery <= 0) return;

    const costRatio = info.costCurrentQuery / info.costLimitPerQuery;

    if (costRatio >= COST_ERROR_THRESHOLD) {
        console.debug(
            `[Query Cost] CRITICAL: ${operationName} — ${info.costCurrentQuery}/${info.costLimitPerQuery}` +
                ` (${(costRatio * 100).toFixed(1)}%)`
        );
    } else if (costRatio >= COST_WARN_THRESHOLD) {
        console.debug(
            `[Query Cost] WARNING: ${operationName} — ${info.costCurrentQuery}/${info.costLimitPerQuery}` +
                ` (${(costRatio * 100).toFixed(1)}%)`
        );
    }
}

function logQueryDebug(operationName: string, info: RateLimitHeaders) {
    const isDebugged = debuggedQueries.has("*") || debuggedQueries.has(operationName);
    if (!isDebugged) return;

    const costPercent =
        info.costLimitPerQuery > 0 ? ((info.costCurrentQuery / info.costLimitPerQuery) * 100).toFixed(1) : "N/A";
    const rateLimitPercent = info.rateLimit > 0 ? ((info.rateLimitConsumed / info.rateLimit) * 100).toFixed(1) : "N/A";

    console.debug(
        `[Query Debug] ${operationName}
` +
            `  Cost: ${info.costCurrentQuery} / ${info.costLimitPerQuery} (${costPercent}%)
` +
            `  Rate: ${info.rateLimitConsumed} / ${info.rateLimit} consumed (${rateLimitPercent}%)
` +
            `  Remaining: ${info.rateLimitRemaining} | Reset in: ${(info.rateLimitTimeUntilReset / 1000).toFixed(1)}s
` +
            `  Slot: ${slotTracker.queryCount} queries fired, ${slotTracker.totalCost} total cost in current window`
    );
}

export const rateLimitLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
        //first only for development, to discuss if enable for dev or even for other stages
        if (process.env.NODE_ENV !== "development") {
            return response;
        }

        const context = operation.getContext();
        const headers: Headers | undefined = context.response?.headers;

        if (!headers) return response;

        const info = parseRateLimitHeaders(headers);
        if (!info) return response;

        const operationName = operation.operationName || "UnknownQuery";

        updateSlotTracker(info);
        logRateLimitStatus(operationName, info);
        logQueryCostStatus(operationName, info);
        logQueryDebug(operationName, info);

        return response;
    });
});

/**
 * First only for local development, discuss whether to enable for all development or even staging environments.
 *
 * Allows developers to track the cost and rate limit impact of their queries in real time, and identify expensive queries.
 *
 * It maintains a tracker for the number of queries and total cost within the current rate limit window, and logs warnings or errors when thresholds are exceeded.
 * If expanded to other environments, instead of console.debug, information can be tracked to telemetry via apmBase.captureError()
 *
 * Specify particular queries to debug or track all queries using a wildcard.
 *
 * The debugged queries are persisted in sessionStorage to survive page reloads during development.
 */
if (process.env.NODE_ENV === "development") {
    (window as any).__enableQueryCostDebug = enableQueryCostDebug;
    (window as any).__disableQueryCostDebug = disableQueryCostDebug;
    (window as any).__getDebuggedQueries = getDebuggedQueries;
    (window as any).__getSlotTracker = getSlotTracker;
}
